/** @file Applies dopePointer effects: playhead, loop range, selection, drag preview, key commands, view. */
import type { ScreenRect } from '@pose2d/editor-geometry/types';
import type { CommandCall, Doc } from '@pose2d/format/types';
import { playhead } from '../../../playback/playhead';
import type { KeyRef } from '../../../stores/types';
import { keyCommands } from '../model/keyCommands';
import { keySelection } from '../model/keySelection';
import type { DopeEffect } from './dopePointer';

/** Where effects land; the canvas hook binds it to the stores, its view state and the editor dispatch. */
export interface DopeTarget {
  doc(): Doc | null;
  selected(): readonly KeyRef[];
  select(keys: KeyRef[]): void;
  setDrag(delta: number): void;
  setBox(rect: ScreenRect | null): void;
  pan(dx: number, dy: number): void;
  dispatch(calls: readonly CommandCall[]): void;
}

/** A committed drag: move (or copy with Alt) the selected keys, then select them where they landed. */
function moveSelected(delta: number, copy: boolean, t: DopeTarget): void {
  const doc = t.doc();
  const selected = t.selected();
  if (doc === null || selected.length === 0) return;
  t.dispatch(copy ? keyCommands.duplicate(doc, selected, delta) : keyCommands.move(selected, delta));
  t.select(keySelection.shift(selected, delta));
}

type Apply<K extends DopeEffect['kind']> = (e: Extract<DopeEffect, { kind: K }>, t: DopeTarget) => void;

const APPLY: { [K in DopeEffect['kind']]: Apply<K> } = {
  frame: (e) => { playhead.setFrame(e.frame); },
  loop: (e) => { playhead.setLoop(e.range); },
  select: (e, t) => { t.select(keySelection.apply(t.selected(), e.refs, e.mode)); },
  drag: (e, t) => { t.setDrag(e.delta); },
  move: (e, t) => { moveSelected(e.delta, e.copy, t); },
  box: (e, t) => { t.setBox(e.rect); },
  pan: (e, t) => { t.pan(e.dx, e.dy); },
};

/** Applies `effects` in order (each reads the selection the previous one left). */
export function applyDopeEffects(effects: readonly DopeEffect[], t: DopeTarget): void {
  for (const e of effects) (APPLY[e.kind] as Apply<DopeEffect['kind']>)(e, t);
}
